import { useState } from "react";
import { Paperclip, Camera, X as XIcon, ShieldCheck } from "lucide-react";
import { lerArquivo, resumo as resumoConversa, descricao as descreverConversa, mesclar } from "../lib/conversaExport";
import { uploadToStorage, getSignedUrl, chaveSegura, ehImagem, reduzirFoto } from "../lib/storage";
import { generateId } from "../utils";
import CameraCaptura, { cameraNoSiteDisponivel } from "./CameraCaptura";

// ═══════════════════════════════════════════════════════════════
// ANEXO DA CONVERSA — a prova de que o contato existiu
// ═══════════════════════════════════════════════════════════════
//
// O comprovante é o export da conversa (formato da plataforma: traz a conversa
// inteira, com hora real e assinatura) e, na falta dele, um print com data e
// hora visíveis. A plataforma exporta a conversa TODA a cada vez, então o
// segundo export é mesclado por cima do primeiro e as linhas repetidas caem
// fora — sem isso a contagem divergiria a cada reanexo.
//
// Vive aqui, e não dentro de um modal, porque agora dois lugares pedem a mesma
// prova: o registro de contato e o registro rápido, que não salva mais sem ela.

const EXT = {
  "image/jpeg": "jpg", "image/jpg": "jpg", "image/png": "png", "image/webp": "webp",
  "image/heic": "heic", "application/pdf": "pdf", "application/json": "json",
};

/** @param valor { path, nome, tipo, conversa, resumo } — null quando não há nada anexado */
export default function AnexoConversa({
  valor, onChange, prefixo = "contatos", refId = "", obrigatorio = false, erro = "",
  titulo = "Comprovante da conversa", ajuda = "", onOcupado,
  // "foto": só imagem (visita presencial). No celular, abre a câmera direto.
  modo = "conversa",
}) {
  const soFoto = modo === "foto";
  const [enviando, setEnviando] = useState(false);
  const [erroLocal, setErroLocal] = useState("");
  const [camera, setCamera] = useState(false);
  const ocupado = (v) => { setEnviando(v); onOcupado && onOcupado(v); };
  const mostrarErro = erro || erroLocal;

  // Nome do arquivo só com caracteres que o Storage aceita (refId pode ser o
  // nome da empresa, com acento e espaço).
  const ref = chaveSegura(refId, "sem-id");

  const anexar = async (arquivo) => {
    if (!arquivo) return;
    let file = arquivo;
    ocupado(true);
    setErroLocal("");
    const ehConversa = /json$/i.test(file.name || "") || file.type === "application/json";
    // Tablet/Android às vezes entrega a foto com type vazio: vale a extensão.
    if (soFoto && !ehImagem(file)) {
      setErroLocal("Aqui vale só foto (fachada, cartão de visita ou com o responsável).");
      ocupado(false);
      return;
    }

    if (ehConversa) {
      const r = await lerArquivo(file);
      if (!r.ok) { setErroLocal(r.erro); ocupado(false); return; }
      const merged = valor?.conversa ? mesclar([valor.conversa, r.conversa]) : r.conversa;
      const res = resumoConversa(merged);
      const path = `${prefixo}/${ref}-${generateId().slice(0, 8)}.json`;
      // Um export sobe COMO VEIO (preserva a assinatura da plataforma); dois ou
      // mais sobem o consolidado, que o navegador não tem como reassinar.
      const conteudo = merged.mesclado ? new Blob([JSON.stringify(merged)], { type: "application/json" }) : file;
      const nome = merged.mesclado ? `conversa-consolidada-${res.fontes}-exports.json` : (file.name || "conversa.json");
      try {
        await uploadToStorage(path, conteudo, "application/json");
        onChange({ path, nome, tipo: "conversa", conversa: merged, resumo: res });
      } catch (err) {
        setErroLocal("Não consegui enviar o arquivo: " + (err.message || "tente de novo"));
      }
      ocupado(false);
      return;
    }

    if (ehImagem(file)) file = await reduzirFoto(file);
    const extNome = file.name?.includes(".") ? file.name.split(".").pop().toLowerCase().replace(/[^a-z0-9]/g, "") : "";
    const ext = EXT[(file.type || "").toLowerCase()] || extNome || "jpg";
    const path = `${prefixo}/${ref}-${generateId().slice(0, 8)}.${ext}`;
    try {
      await uploadToStorage(path, file, file.type || "image/jpeg");
      onChange({ path, nome: file.name || `comprovante.${ext}`, tipo: soFoto ? "foto" : "print", conversa: null, resumo: null });
    } catch (err) {
      setErroLocal("Não consegui enviar o arquivo: " + (err.message || "tente de novo"));
    }
    ocupado(false);
  };

  const ver = async () => {
    const win = window.open("", "_blank");
    const url = await getSignedUrl(valor?.path);
    if (!url) { if (win) win.close(); return; }
    if (win) win.location.href = url; else window.open(url, "_blank");
  };

  return (
    <div style={{
      padding: "11px 13px", borderRadius: 9,
      background: valor?.path ? "#f0fdfa" : (mostrarErro ? "#fef2f2" : "#f8fafc"),
      border: `1px solid ${valor?.path ? "#5eead4" : (mostrarErro ? "#fecaca" : "#e2e8f0")}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 3 }}>
        <Camera size={13} color="#7c3aed" />
        {titulo} {obrigatorio && <span style={{ color: "#dc2626" }}>*</span>}
      </div>
      <div style={{ fontSize: 10.5, color: "#64748b", lineHeight: 1.45, marginBottom: 8 }}>
        {ajuda || "Exporte a conversa na plataforma (botão \"export\") e anexe o arquivo — vem a conversa inteira, com hora real e assinatura. Print também vale, desde que a data e a hora apareçam na imagem."}
      </div>

      {valor?.path ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 6, background: "#fff", border: "1px solid #5eead4", fontSize: 11, color: "#0f766e", fontWeight: 600, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <Paperclip size={11} /> {valor.nome}
          </span>
          <button type="button" onClick={ver} style={btn}>Ver</button>
          {valor.tipo === "conversa" && (
            <label style={{ ...btn, display: "flex", alignItems: "center", gap: 4, borderColor: "#99f6e4", color: "#0f766e", cursor: enviando ? "wait" : "pointer" }}>
              <Paperclip size={11} /> {enviando ? "Juntando..." : "Juntar outro export"}
              <input type="file" accept="application/json,.json" disabled={enviando}
                onChange={(e) => { anexar(e.target.files?.[0]); e.target.value = ""; }} style={{ display: "none" }} />
            </label>
          )}
          <button type="button" onClick={() => onChange(null)} style={{ ...btn, display: "flex", alignItems: "center", gap: 4, borderColor: "#fecaca", color: "#b91c1c" }}>
            <XIcon size={11} /> Trocar
          </button>
        </div>
      ) : soFoto ? (
        // Dois botões: sem `capture`, o Android abre só o seletor do Google
        // Fotos (sem câmera) — e foto que está só na nuvem pode não carregar.
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {cameraNoSiteDisponivel() ? (
            // Câmera na própria página: o app de câmera do Android fazia o
            // navegador ser fechado, e a aba voltava recarregada, sem nada.
            <button type="button" disabled={enviando} onClick={() => setCamera(true)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 7, border: "1px solid #d97706", background: "#fffbeb", color: "#b45309", fontSize: 12, fontWeight: 700, cursor: enviando ? "wait" : "pointer" }}>
              <Camera size={14} /> {enviando ? "Enviando..." : "Tirar foto agora"}
            </button>
          ) : (
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 7, border: "1px solid #d97706", background: "#fffbeb", color: "#b45309", fontSize: 12, fontWeight: 700, cursor: enviando ? "wait" : "pointer" }}>
              <Camera size={14} />
              {enviando ? "Enviando..." : "Tirar foto agora"}
              <input type="file" accept="image/*" capture="environment" disabled={enviando}
                onChange={(e) => { anexar(e.target.files?.[0]); e.target.value = ""; }} style={{ display: "none" }} />
            </label>
          )}
          <CameraCaptura aberta={camera} onFechar={() => setCamera(false)} onFoto={anexar} />
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 7, border: "1px dashed #94a3b8", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: enviando ? "wait" : "pointer" }}>
            <Paperclip size={13} /> Escolher da galeria
            <input type="file" accept="image/*" disabled={enviando}
              onChange={(e) => { anexar(e.target.files?.[0]); e.target.value = ""; }} style={{ display: "none" }} />
          </label>
        </div>
      ) : (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 7, border: "1px dashed #94a3b8", background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600, cursor: enviando ? "wait" : "pointer" }}>
          <Paperclip size={12} />
          {enviando ? "Enviando..." : "Anexar conversa exportada ou print"}
          <input type="file" accept="image/*,application/pdf,application/json,.json" disabled={enviando}
            onChange={(e) => { anexar(e.target.files?.[0]); e.target.value = ""; }} style={{ display: "none" }} />
        </label>
      )}

      {mostrarErro && <div style={{ fontSize: 10.5, color: "#dc2626", marginTop: 6, fontWeight: 600 }}>{mostrarErro}</div>}

      {valor?.resumo && (
        <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 7, background: "#f0fdfa", border: "1px solid #99f6e4", fontSize: 10.5, color: "#0f766e", lineHeight: 1.5 }}>
          <strong>Conversa com {valor.resumo.contato}</strong>
          {valor.resumo.numero ? ` (${valor.resumo.numero})` : ""} — {descreverConversa(valor.resumo)}.
          {valor.resumo.atendente ? ` Atendida por ${valor.resumo.atendente}.` : ""}
          {valor.resumo.mesclado
            ? ` Consolidado de ${valor.resumo.fontes} exports (linhas repetidas descartadas).`
            : (valor.resumo.assinado ? " Arquivo assinado pela plataforma." : " Atenção: arquivo sem assinatura.")}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "flex-start", gap: 5, fontSize: 9.5, color: "#64748b", marginTop: 8, lineHeight: 1.4 }}>
        <ShieldCheck size={11} color="#059669" style={{ flexShrink: 0, marginTop: 1 }} />
        O comprovante fica em servidor privado, criptografado, e só a equipe com acesso ao CRM consegue abrir.
      </div>
    </div>
  );
}

const btn = {
  padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0",
  background: "#fff", color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer",
};
